import React, { useState } from 'react';
import { STATUS_GROUPS } from '../../constants/statusGroups.constants';
import './StatusGroupFilter.css';

interface StatusGroupFilterProps {
  visibleGroups: number[]; // Currently visible status group IDs (empty = all visible)
  onVisibleGroupsChange: (groupIds: number[]) => void;
  statusSortOrder: 'asc' | 'desc';
  onStatusSortOrderChange: (order: 'asc' | 'desc') => void;
  channelsByGroup: Map<number, number>; // Map of group ID to count of channels
  isExpanded?: boolean; // Control expanded state from parent
  onToggleExpanded?: () => void; // Callback for toggle
  onToggleAll?: () => void; // Callback to toggle all groups
  disabled?: boolean; // When true, filter is disabled (e.g., during database search)
}

/**
 * StatusGroupFilter - Component to filter tracker channels by status groups
 *
 * Features:
 * - Show/hide status groups
 * - Toggle between ascending/descending status order
 * - Visual indicators for each group (color, icon, count)
 * - Quick actions: Show All, Hide All
 */
export const StatusGroupFilter: React.FC<StatusGroupFilterProps> = ({
  visibleGroups,
  onVisibleGroupsChange,
  statusSortOrder: _statusSortOrder,
  onStatusSortOrderChange: _onStatusSortOrderChange,
  channelsByGroup,
  isExpanded: controlledExpanded,
  onToggleExpanded: _onToggleExpanded,
  onToggleAll,
  disabled = false
}) => {
  const [internalExpanded, _setInternalExpanded] = useState(true);

  // Use controlled or internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  // Check if a group is visible
  const isGroupVisible = (groupId: number): boolean => {
    return visibleGroups.length === 0 || visibleGroups.includes(groupId);
  };

  // Simple toggle for checkbox - always just toggle on/off
  const toggleGroupCheckbox = (groupId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    // Stop propagation to prevent label click
    e.stopPropagation();

    if (visibleGroups.length === 0) {
      // If showing all, unchecking means show all EXCEPT this one
      const allGroupIds = STATUS_GROUPS.map(g => g.id);
      onVisibleGroupsChange(allGroupIds.filter(id => id !== groupId));
    } else if (visibleGroups.includes(groupId)) {
      // Remove this group from visible list
      const newGroups = visibleGroups.filter(id => id !== groupId);
      // If empty after removing, go back to showing all
      onVisibleGroupsChange(newGroups.length === 0 ? [] : newGroups);
    } else {
      // Add this group to visible list
      const newGroups = [...visibleGroups, groupId];
      // If we've selected all groups, simplify to showing all (empty array)
      const allGroupIds = STATUS_GROUPS.map(g => g.id);
      if (newGroups.length === allGroupIds.length) {
        onVisibleGroupsChange([]);
      } else {
        onVisibleGroupsChange(newGroups);
      }
    }
  };

  // Handle item click (not checkbox)
  const handleItemClick = (groupId: number) => {
    // When showing all groups, clicking any item should isolate it
    if (visibleGroups.length === 0) {
      onVisibleGroupsChange([groupId]);
      return;
    }

    // When filtering (some groups selected), toggle this group in/out
    if (visibleGroups.includes(groupId)) {
      // Remove this group from visible list
      const newGroups = visibleGroups.filter(id => id !== groupId);
      // If empty after removing, go back to showing all
      onVisibleGroupsChange(newGroups.length === 0 ? [] : newGroups);
    } else {
      // Add this group to visible list
      const newGroups = [...visibleGroups, groupId];
      // If we've selected all groups, simplify to showing all (empty array)
      const allGroupIds = STATUS_GROUPS.map(g => g.id);
      if (newGroups.length === allGroupIds.length) {
        onVisibleGroupsChange([]);
      } else {
        onVisibleGroupsChange(newGroups);
      }
    }
  };

  return (
    <>
      {/* Expanded filter content */}
      {isExpanded && (
        <div className={`status-group-filter__expanded ${disabled ? 'status-group-filter--disabled' : ''}`}>
          {/* Status group list */}
          <div className="status-group-filter__list">
            {STATUS_GROUPS.map(group => {
              const count = channelsByGroup.get(group.id) || 0;
              const visible = isGroupVisible(group.id);
              const isItemDisabled = disabled || count === 0;

              return (
                <div
                  key={group.id}
                  className={`status-group-filter__item ${visible ? 'checked' : ''} ${isItemDisabled ? 'disabled' : ''}`}
                  title={disabled ? 'Filtre désactivé pendant la recherche' : `${group.description} (${count} affaire${count > 1 ? 's' : ''})`}
                  onClick={() => !isItemDisabled && handleItemClick(group.id)}
                  style={{ cursor: isItemDisabled ? 'not-allowed' : 'pointer' }}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    className="status-group-filter__checkbox"
                    checked={visible}
                    onChange={(e) => !disabled && toggleGroupCheckbox(group.id, e)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isItemDisabled}
                  />

                  {/* Visual indicator */}
                  <span
                    className="status-group-filter__indicator"
                    style={{
                      backgroundColor: group.color,
                      opacity: visible ? 1 : 0.3
                    }}
                  />

                  {/* Label */}
                  <span className="status-group-filter__label">
                    {group.label}
                  </span>

                  {/* Count */}
                  <span className="status-group-filter__count">
                    {count}
                  </span>
                </div>
              );
            })}

            {/* Toggle all button at end of list */}
            {onToggleAll && (
              <button
                className="status-group-filter__action-btn"
                onClick={onToggleAll}
                disabled={disabled}
              >
                {visibleGroups.length === 0 ? 'Tout masquer' : 'Tout afficher'}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Export helper component for header controls (now empty, kept for compatibility)
export const StatusGroupFilterHeader: React.FC<{
  visibleGroups: number[];
  totalChannels: number;
  totalVisibleChannels: number;
  isExpanded: boolean;
  onToggle: () => void;
  onToggleAll: () => void;
}> = () => {
  // Header controls removed - action button now lives in StatusGroupFilter list
  return null;
};

export default StatusGroupFilter;
